import { Analytics } from '@adornis/analytics/db/analytics.js';
import type { EventLog } from '@adornis/analytics/db/eventlog.js';
import { QueueJob } from '@adornis/analytics/db/queue-job.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { PageLoadEventLog } from '../eventlogs/PageLoadEventlog.js';

/**
 * @entity ChancenLoginAnalytics
 */
@Entity()
export class ChancenUserActivityAnalytics extends Analytics {
  public static override _class = 'ChancenLoginAnalytics';
  @Field(() => [ServiceLogins], { default: v => v ?? [] })
  public serviceLogins!: ServiceLogins[];

  public static override async createQueueJob(log: EventLog) {
    if (![PageLoadEventLog._class].includes(log._class) || !log.userID) return;
    const analytic: ChancenUserActivityAnalytics = await this.retrieveOrCreateAnalytic<ChancenUserActivityAnalytics>(
      log.userID,
      {},
    );
    await new QueueJob({
      analyticIDs: [analytic._id],
      analyticsClass: ChancenUserActivityAnalytics._class,
      urgency: 10,
      targetDate: new Date(),
    }).create();
  }
  public static override async updateAnalytic(queueJob: QueueJob) {
    logger.debug({ queueJob }, 'updating ChancenLoginAnalytics');
    const service = projectConfig.get('SERVICE_NAME');
    const analytics = await this.retrieveAnalytics<ChancenUserActivityAnalytics>({
      _id: { $in: queueJob.analyticIDs },
    });
    for (const analytic of analytics) {
      const sls = analytic.serviceLogins.find(s => s.service === service);
      if (sls) {
        sls.loginDates.push(new Date());
      } else {
        analytic.serviceLogins.push(new ServiceLogins({ service: service, loginDates: [new Date()] }));
      }
      await this.saveEntityWithoutContext(analytic);
    }
    logger.debug({ analytics }, 'updated ChancenLoginAnalytics');
  }
}

/**
 * @entity ServiceLoginStats
 */
@Entity()
export class ServiceLogins extends AdornisEntity {
  public static override _class = 'ServiceLoginStats';
  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;
  @Field(() => String)
  public service!: string;
  @Field(() => [Date], { default: v => v ?? [] })
  public loginDates!: Date[];
}
